import React, { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';
import { ProductGroup, ProductGroupCreation, ProductGroupUpdate } from '../models/ProductGroup';
import { Product } from '../models/Product';

interface ProductGroupContextType {
  // Admin functions
  createProductGroup: (groupData: ProductGroupCreation) => Promise<ProductGroup>;
  getProductGroups: (page?: number, limit?: number) => Promise<{
    groups: ProductGroup[];
    total: number;
    page: number;
    totalPages: number;
  }>;
  getProductGroup: (id: number) => Promise<ProductGroup>;
  updateProductGroup: (id: number, groupData: ProductGroupUpdate) => Promise<ProductGroup>;
  deleteProductGroup: (id: number) => Promise<void>;
  addProductToGroup: (groupId: number, productId: number, order?: number) => Promise<ProductGroup>;
  removeProductFromGroup: (groupId: number, productId: number) => Promise<ProductGroup>;
  getGroupsForProduct: (productId: number) => Promise<ProductGroup[]>;
  
  // Public functions
  getSuggestedProducts: (productId: number, limit?: number) => Promise<Product[]>;
}

const ProductGroupContext = createContext<ProductGroupContextType | undefined>(undefined);

export const useProductGroup = () => {
  const context = useContext(ProductGroupContext);
  if (!context) {
    throw new Error('useProductGroup must be used within a ProductGroupProvider');
  }
  return context;
};

interface ProductGroupProviderProps {
  children: ReactNode;
}

export const ProductGroupProvider: React.FC<ProductGroupProviderProps> = ({ children }) => {
  const reactAppApiUrl = process.env.REACT_APP_API_URL;
  if (!reactAppApiUrl) {
    throw new Error('REACT_APP_API_URL is not set');
  }

  const apiUrl = new URL('product-groups/', reactAppApiUrl).toString();

  /**
   * Creates a new product group
   */
  const createProductGroup = async (groupData: ProductGroupCreation): Promise<ProductGroup> => {
    try {
      const response = await api.post(apiUrl, groupData);
      return response.data.group;
    } catch (error: any) {
      console.error('Failed to create product group', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Gets all product groups with pagination
   */
  const getProductGroups = async (page = 1, limit = 10): Promise<{
    groups: ProductGroup[];
    total: number;
    page: number;
    totalPages: number;
  }> => {
    try {
      const response = await api.get(apiUrl, {
        params: { page, limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch product groups', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Gets a product group by ID with its products
   */
  const getProductGroup = async (id: number): Promise<ProductGroup> => {
    try {
      const response = await api.get(`${apiUrl}${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch product group', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Updates a product group
   */
  const updateProductGroup = async (id: number, groupData: ProductGroupUpdate): Promise<ProductGroup> => {
    try {
      const response = await api.put(`${apiUrl}${id}`, groupData);
      return response.data.group;
    } catch (error: any) {
      console.error('Failed to update product group', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Deletes a product group
   */
  const deleteProductGroup = async (id: number): Promise<void> => {
    try {
      await api.delete(`${apiUrl}${id}`);
    } catch (error: any) {
      console.error('Failed to delete product group', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Adds a product to a group
   */
  const addProductToGroup = async (groupId: number, productId: number, order?: number): Promise<ProductGroup> => {
    try {
      const response = await api.post(
        `${apiUrl}${groupId}/products/${productId}`, 
        order !== undefined ? { order } : {}
      );
      return response.data.group;
    } catch (error: any) {
      console.error('Failed to add product to group', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Removes a product from a group
   */
  const removeProductFromGroup = async (groupId: number, productId: number): Promise<ProductGroup> => {
    try {
      const response = await api.delete(`${apiUrl}${groupId}/products/${productId}`);
      return response.data.group;
    } catch (error: any) {
      console.error('Failed to remove product from group', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Gets all groups that contain a specific product
   */
  const getGroupsForProduct = async (productId: number): Promise<ProductGroup[]> => {
    try {
      const response = await api.get(`${apiUrl}product/${productId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch groups for product', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Gets suggested products for a specific product
   * This is a public endpoint that doesn't require authentication
   */
  const getSuggestedProducts = async (productId: number, limit = 5): Promise<Product[]> => {
    try {
      const response = await api.get(`${apiUrl}product/${productId}/suggested`, {
        params: { limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch suggested products', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  return (
    <ProductGroupContext.Provider
      value={{
        createProductGroup,
        getProductGroups,
        getProductGroup,
        updateProductGroup,
        deleteProductGroup,
        addProductToGroup,
        removeProductFromGroup,
        getGroupsForProduct,
        getSuggestedProducts,
      }}
    >
      {children}
    </ProductGroupContext.Provider>
  );
};
